import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import updateUserContext from '../../redux/actions/user';
import NodeActions from '../../redux/actions/input-nodes';

import { crudConfig, uiConfig } from '../../conf';

//UI Components
import { Button } from '../../../components/Button';
import Table from '../../../components/Table';
import AddEdit from './addEdit';
import Delete from './delete';

import { translateUIConfigForTable, translateUIConfigForAddEdit, StateReconciler } from '../../../common/utility';

const { READ_ONLY_FEATURES } = process.env;

const InputNodeManager = ({ userContext }) => {
    const dispatch = useDispatch();
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [fatalError, setFatalError] = useState(null);
    const saveDisabled = READ_ONLY_FEATURES === 'true';

    let { sessionToken, ...context } = userContext;
    useEffect(() => {
        dispatch(updateUserContext(context, sessionToken))
            .then(() => {
                return dispatch(NodeActions(crudConfig).fetchAll()).then(() => {
                    setFatalError(null);
                });
            })
            .catch((err) => {
                setFatalError(err);
            });
    }, [sessionToken]);

    let tableData = [];
    const { data, updated, deleted } = useSelector((state) =>
        StateReconciler.object(state.input_nodes, uiConfig.primaryKey, ['updated', 'deleted'])
    );
    const { columns, actions } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);
    if (actions != null) {
        tableData = Object.values(data).map((v) => ({
            ...v,
            DataTableActions: actions.map((i) => ({
                ...i,
                action: () => i.action(v[uiConfig.primaryKey])
            }))
        }));
    }

    const unmountDataCheck = (updated, deleted) => {
        if (typeof updated === 'object') {
            if (Object.keys(updated).length > 0 || deleted.length > 0) {
                return 'You have unsaved changed, are you sure you wish to leave';
            }
        }
    };

    useEffect(() => {
        window.onbeforeunload = () => unmountDataCheck(updated, deleted);
    }, [updated, deleted]);

    useEffect(() => {
        if (fatalError !== null) {
            throw fatalError;
        }
    });

    useEffect(() => {
        return () => {
            unmountDataCheck();
        };
    }, []);

    const maybeFetchEditData = () => {
        if (editing != 'new') {
            if (typeof data[editing] == 'object') {
                return data[editing];
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
            }
        } else {
            return typeof uiConfig.newDoc == 'object' ? uiConfig.newDoc : {};
        }
    };

    const fetchDeleteData = () => {
        return data[deleting];
    };

    const closeAddEdit = () => {
        setEditing(null);
    };

    const onDelete = () => {
        if (saveDisabled) {
            console.warn("Authoring Interface is currently read-only.");
            return;
        }
        return new Promise((resolve, reject) => {
            dispatch(NodeActions(crudConfig).delete(deleting))
                .then((resp) => {
                    resolve(resp);
                })
                .catch((e) => {
                    reject(e);
                });
        });
    };

    const closeDelete = () => {
        setDeleting(null);
    };

    const isExistingDoc = (doc) => {
        return (
            Object.values(data).find((v) => {
                return (
                    v.nodePath === doc.nodePath &&
                    v.templateClassName === doc.templateClassName &&
                    v.attributeClassName === doc.attributeClassName &&
                    v.nodeId !== doc.nodeId
                );
            }) !== undefined
        );
    };

    const applyChanges = (updatedDoc) => {
        // Check for is new
        // alert("Deferred Save not currently implemented, this is local only edits");
        return new Promise((resolve, reject) => {
            if (editing === 'new') {
                if (!isExistingDoc(updatedDoc)) {
                    dispatch(NodeActions(crudConfig).insert(updatedDoc))
                        .then((resp) => {
                            resolve(resp);
                        })
                        .catch((e) => {
                            reject(e);
                        });
                } else {
                    reject('Matching document already exists.');
                }
            } else {
                if (!isExistingDoc(updatedDoc)) {
                    dispatch(NodeActions(crudConfig).update(updatedDoc[uiConfig.primaryKey], updatedDoc))
                        .then((resp) => {
                            resolve(resp);
                        })
                        .catch((e) => {
                            reject(e);
                        });
                } else {
                    reject('Matching document already exists.');
                }
            }
        });
    };

    const controlButtons = [];
    if (crudConfig.functions.insert) {
        controlButtons.push(
            <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
                Add New Input Node
            </Button>
        )
    }

    return (
        <div>
            {tableData.length > 0 && (
                <>
                    <Table
                        withHeader={true}
                        columns={columns}
                        data={tableData}
                        withActions={actions !== null}
                        id={'input-node-manager'}
                        label={'Input Nodes'}
                        showControlDeck={true}
                        paginated={true}
                        controlButtons={controlButtons}
                    />
                    {editing != null && (
                        <AddEdit
                            formModel={translateUIConfigForAddEdit(uiConfig)}
                            isNew={editing === 'new'}
                            saveDisabled={saveDisabled}
                            data={maybeFetchEditData()}
                            closeDialog={closeAddEdit}
                            onApply={applyChanges}
                        />
                    )}
                    {deleting != null && (
                        <Delete data={fetchDeleteData()} closeDialog={closeDelete} onDelete={onDelete} />
                    )}
                </>
            )}
        </div>
    );
};

export default InputNodeManager;
